import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Chip,
    IconButton,
    TextField,
    FormControlLabel,
    Checkbox,
    Divider
} from '@mui/material';
import { 
    LocationOn, 
    Close, 
    Warning, 
    CheckCircle, 
    Error, 
    GpsFixed, 
    Edit,
    Info
} from '@mui/icons-material';

const LocationVerificationDialog = ({ 
    open, 
    onClose, 
    onConfirm, 
    onRetryGPS,
    locationData,
    addressDetails
}) => {
    const [userConfirmed, setUserConfirmed] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editedAddress, setEditedAddress] = useState('');
    const [supervisorOverride, setSupervisorOverride] = useState(false);

    const handleConfirm = () => {
        if (editMode && editedAddress.trim()) {
            onConfirm({ 
                confirmed: true, 
                editedAddress: editedAddress.trim(),
                supervisorOverride: supervisorOverride
            });
        } else {
            onConfirm({ 
                confirmed: userConfirmed,
                supervisorOverride: supervisorOverride
            });
        }
        handleClose();
    };

    const handleClose = () => {
        setUserConfirmed(false);
        setEditMode(false);
        setEditedAddress('');
        setSupervisorOverride(false);
        onClose();
    };

    const handleRetry = () => {
        onRetryGPS();
        handleClose();
    };

    if (!locationData) return null;

    const { 
        coordinates, 
        withinServiceArea, 
        accuracy, 
        accuracyStatus, 
        source 
    } = locationData;

    // Determine the severity of issues
    const hasAccuracyIssue = accuracyStatus === 'low' && accuracy > 300;
    const hasServiceAreaIssue = !withinServiceArea;
    const hasHighAccuracyIssue = accuracy > 1000;
    const requiresSupervisorOverride = hasServiceAreaIssue || hasHighAccuracyIssue;

    const getAccuracyColor = (status) => {
        switch (status) {
            case 'high': return 'success';
            case 'medium': return 'warning';
            case 'low': return 'error';
            default: return 'default';
        }
    };

    const getAccuracyIcon = (status) => {
        switch (status) {
            case 'high': return <CheckCircle />;
            case 'medium': return <Warning />;
            case 'low': return <Error />;
            default: return <LocationOn />;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{
                pb: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn color="primary" />
                    <Typography variant="h6">Verify Location</Typography>
                </Box>
                <IconButton edge="end" onClick={handleClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ py: 3 }}>
                {/* Location Status Summary */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Location Status
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* GPS Accuracy */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: '80px' }}>
                                Accuracy:
                            </Typography>
                            <Chip
                                icon={getAccuracyIcon(accuracyStatus)}
                                label={accuracy ? `±${Math.round(accuracy)}m` : 'Manual Entry'}
                                color={getAccuracyColor(accuracyStatus)}
                                size="small"
                                variant="outlined"
                            />
                        </Box>

                        {/* Service Area */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: '80px' }}>
                                Location:
                            </Typography>
                            <Chip
                                icon={withinServiceArea ? <CheckCircle /> : <Warning />}
                                label={withinServiceArea ? 'Within Arizona' : 'Outside Arizona'}
                                color={withinServiceArea ? 'success' : 'warning'}
                                size="small"
                                variant="outlined"
                            />
                        </Box>

                        {/* Source */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: '80px' }}>
                                Source:
                            </Typography>
                            <Chip
                                icon={source === 'gps' ? <GpsFixed /> : <Edit />}
                                label={source === 'gps' ? 'GPS Location' : 'Manual Entry'}
                                color={source === 'gps' ? 'primary' : 'default'}
                                size="small"
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Address Information */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Detected Address
                    </Typography>
                    
                    {editMode ? (
                        <TextField
                            fullWidth
                            label="Edit Address"
                            value={editedAddress}
                            onChange={(e) => setEditedAddress(e.target.value)}
                            multiline
                            rows={2}
                            variant="outlined"
                            placeholder="Enter the correct address..."
                        />
                    ) : (
                        <Box sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50', 
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.200'
                        }}>
                            <Typography variant="body2">
                                {addressDetails?.addressLine || 'Address not available'}
                                {addressDetails?.city && `, ${addressDetails.city}`}
                                {addressDetails?.state && `, ${addressDetails.state}`}
                                {addressDetails?.postalCode && ` ${addressDetails.postalCode}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Coordinates: {coordinates?.lat?.toFixed(6)}, {coordinates?.lng?.toFixed(6)}
                            </Typography>
                        </Box>
                    )}
                    
                    <Button
                        startIcon={<Edit />}
                        onClick={() => {
                            setEditMode(!editMode);
                            if (!editMode) {
                                setEditedAddress(
                                    `${addressDetails?.addressLine || ''}, ${addressDetails?.city || ''}, ${addressDetails?.state || ''} ${addressDetails?.postalCode || ''}`.trim()
                                );
                            }
                        }}
                        sx={{ mt: 1 }}
                        size="small"
                    >
                        {editMode ? 'Cancel Edit' : 'Edit Address'}
                    </Button>
                </Box>

                {/* Warnings and Alerts */}
                {hasServiceAreaIssue && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Outside Service Area:</strong> This location appears to be outside Arizona. 
                            Please verify this is the correct location for your service call.
                        </Typography>
                    </Alert>
                )}

                {hasAccuracyIssue && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Low GPS Accuracy:</strong> GPS signal is weak (±{Math.round(accuracy)}m). 
                            Consider trying to get GPS again or manually verify the location.
                        </Typography>
                    </Alert>
                )}

                {hasHighAccuracyIssue && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Very Poor GPS:</strong> GPS accuracy is very poor (±{Math.round(accuracy)}m). 
                            This location may be significantly incorrect.
                        </Typography>
                    </Alert>
                )}

                {/* Information Box */}
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        Please confirm this is the correct pickup location for your tow request. 
                        Incorrect locations can cause delays in service.
                    </Typography>
                </Alert>

                {/* User Confirmation */}
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={userConfirmed}
                                onChange={(e) => setUserConfirmed(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I confirm this is the correct pickup location
                            </Typography>
                        }
                    />
                </Box>

                {/* Supervisor Override for critical issues */}
                {requiresSupervisorOverride && (
                    <Box sx={{ mt: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={supervisorOverride}
                                    onChange={(e) => setSupervisorOverride(e.target.checked)}
                                    color="warning"
                                />
                            }
                            label={
                                <Typography variant="body2" color="warning.main">
                                    Supervisor override - I understand this location has issues
                                </Typography>
                            }
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{
                px: 3,
                pb: 2,
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                gap: 1
            }}>
                {/* Retry GPS Button */}
                {source === 'gps' && hasAccuracyIssue && (
                    <Button
                        startIcon={<GpsFixed />}
                        onClick={handleRetry}
                        color="primary"
                        variant="outlined"
                    >
                        Retry GPS
                    </Button>
                )}

                {/* Cancel Button */}
                <Button
                    onClick={handleClose}
                    color="inherit"
                >
                    Cancel
                </Button>

                {/* Confirm Button */}
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={
                        !userConfirmed || 
                        (requiresSupervisorOverride && !supervisorOverride) ||
                        (editMode && !editedAddress.trim())
                    }
                    sx={{ ml: 'auto' }}
                >
                    Confirm Location
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LocationVerificationDialog;